<template>
  <div class="px-3 px-md-5 py-1" :class="{ 'bg-theme-danger': showErrors, 'bg-light': !showErrors }">
    <div v-if="errors">
      <div class="sub-lead" v-for="error in errors">
        {{ $t(error.path, error.values) }}
      </div>
    </div>
    <div class="d-flex justify-content-between flex-wrap">
      <span class="sub-lead">{{ crossCounter }}</span>
      <div v-if="contest.markingType.quadraticVoting" class="sub-lead">
        {{ $t("js.components.AVSubmissionHelper.remaining_credits") }}
        <strong>{{ remainingVoiceCredits }}</strong> / <strong>{{ contest.markingType.quadraticVotingVoiceCredits }}</strong> 
      </div>
    </div>
    <div class="d-flex justify-content-start flex-wrap">
      <span class="sub-lead">
        {{ $t("js.ballot.selected") }}: {{ selected }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import SelectionPileValidator from "@assemblyvoting/js-client/dist/lib/validators/selectionPileValidator";
import BelgiumBallotValidator from "@assemblyvoting/js-client/dist/lib/validators/belgiumBallotValidator";
import type { PropType, SelectionPile, ContestError, ContestContent } from "@/types";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();

const props = defineProps({
  contest: {
    type: Object as PropType<ContestContent>,
    required: true,
  },
  activeSelectionPile: {
    type: Object as PropType<SelectionPile>,
    required: true,
  },
  includeLazyErrors: {
    type: Boolean,
    default: false,
  },
}); 

const crossCounter = computed(() => {
  let counter = null;
  const minimumVotes = props.contest.markingType?.minMarks;
  const maximumVotes = props.contest.markingType?.maxMarks;
  const voteVariation = props.contest.markingType?.voteVariation;
  if (voteVariation === "ranked") {
    counter = minimumVotes === maximumVotes
      ? t("js.ballot.rank_x_options", { n: minimumVotes, plural: minimumVotes })
      : t("js.ballot.rank_between_x_and_y_options", { x: minimumVotes, y: maximumVotes });
  } else {
    counter = minimumVotes === maximumVotes
      ? t("js.ballot.choose_x_options", { n: minimumVotes, plural: minimumVotes })
      : t("js.ballot.choose_between_x_and_y_options", { x: minimumVotes, y: maximumVotes });
  }
  return counter;
});

const selected = computed(() => {
  if (!props.activeSelectionPile) return 0;
  return props.activeSelectionPile.optionSelections.length + (props.activeSelectionPile.explicitBlank && 1);
});

const errors = computed(() => {
  if (!props.activeSelectionPile) return [];
  const customvalidators: any[] = [];

  const errors = new SelectionPileValidator(props.contest).validate(props.activeSelectionPile, props.includeLazyErrors).map((error: ContestError) => {
    if (error.keys) {
      // We do this in case a key contains a locale string
      Object.keys(error.keys).forEach((key) => {
        if (typeof error.keys[key] === "object") {
          error.keys[key] = error.keys[key][locale.value];
        }
      });
    }

    switch (error.message) {
      case "blank":
        return {
          path: "js.error.default.blank_combined_with_other_options",
          values: {},
        }
      case "too_many":
        return {
          path: "js.error.default.too_many_crosses",
          values: { maximum: props.contest.markingType?.maxMarks },
        }
      case "exceeded_list_limit":
        return {
          path: "js.components.AVSubmissionHelper.errors.exceeded_list_limit",
          values: error.keys,
        }
      case "below_list_limit":
        return {
          path: "js.components.AVSubmissionHelper.errors.below_list_limit",
          values: error.keys,
        }
      case "write_in_too_long":
      case "write_in_required":
      case "write_in_not_supported":
      case "write_in_empty":
      case "too_many_credits":
        return {
          path: `js.components.AVSubmissionHelper.errors.${error.message}`,
          values: {},
        }
    }
    return {
      path: `js.error.${props.contest.markingType?.voteVariation}.${error.message}`,
      values: {},
    }
  });

  if (props.contest.customRulesets.includes("belgian_ballot_rules")) {
    customvalidators.push(new BelgiumBallotValidator(props.contest));
  }

  const customErrors = customvalidators.flatMap(validator => {
    return validator.validate(props.activeSelectionPile).map((error: ContestError) => {
      return {
        path: `js.components.AVSubmissionHelper.errors.${error.message}`,
        values: {},
      }
    });
  });

  const combinedErrors = [...errors, ...customErrors];

  return [...combinedErrors];
});

const showErrors = computed(() => errors.value.length > 0);

const remainingVoiceCredits = computed(() => {
  const counts = new Map<string, number>();
  let usedCredits: number = 0;

  for (const selection of props.activeSelectionPile.optionSelections) {
    counts.set(selection.reference, (counts.get(selection.reference) ?? 0) + 1);
  }

  for (const count of counts.values()) {
    usedCredits += count * count;
  }

  return props.contest.markingType.quadraticVotingVoiceCredits - usedCredits;
});
</script>
